import type { addToCartArgsType } from 'components/shared/Cart/types';
import { pickupVariantGid } from 'content/products/ring-pro/bestBuyPickupVariants';
import { BB_LINE_PROPS } from './config';
import { pickupVariantIdFor } from './pickupCatalogue';
import type { PickupAddDecision } from './pickupSelection';

let warnedMissingVariant = false;

/** The Best Buy pickup variant gid for an ops SKU. Null (the ring keeps its
 * normal variant) off PRODUCTION, where the dev Shopify store has no pickup
 * product, and when the catalogue lists no variant for the SKU: the order
 * still links to its hold through the line attributes. */
const pickupVariantGidFor = (sku: string): string | null => {
  if (process.env.NEXT_PUBLIC_ENVIRONMENT !== 'PRODUCTION') return null;
  const variantId = pickupVariantIdFor(sku);
  if (variantId) return pickupVariantGid(variantId);
  if (!warnedMissingVariant && process.env.NODE_ENV !== 'production') {
    warnedMissingVariant = true;
    console.warn(
      `[best-buy-pickup] no pickup variant for ${sku}; using the normal Ring PRO variant`,
    );
  }
  return null;
};

/** Adds the pickup line properties to the ring (the last line, the bundle
 * parent). Any other decision returns the lines untouched. */
export const applyPickupToLineItems = (
  lineItems: addToCartArgsType[],
  decision: PickupAddDecision,
): addToCartArgsType[] => {
  if (decision.kind !== 'pickup' || lineItems.length === 0) return lineItems;
  const { choice } = decision;
  const lastIndex = lineItems.length - 1;
  const pickupVariant = pickupVariantGidFor(choice.sku);
  return lineItems.map((item, index) =>
    index === lastIndex
      ? {
          ...item,
          ...(pickupVariant ? { shopifyVariantId: pickupVariant } : {}),
          params: {
            ...item.params,
            // One ring per pickup order (a hold is one unit): the cart's
            // max-quantity guards then refuse a second one.
            maxQuantity: 1,
            noteAttributes: [
              ...(item.params?.noteAttributes ?? []),
              { key: BB_LINE_PROPS.LOCATION_ID, value: choice.location_id },
              { key: BB_LINE_PROPS.STORE, value: choice.name },
              { key: BB_LINE_PROPS.ZIP, value: choice.zip },
              { key: BB_LINE_PROPS.SKU, value: choice.sku },
              ...(choice.pickup_date
                ? [{ key: BB_LINE_PROPS.DATE, value: choice.pickup_date }]
                : []),
            ],
          },
        }
      : item,
  );
};
